<!-- FloatingVideoPlayer.vue -->
<template>
  <div
    class="floating-video-container"
    :class="{ 'minimized': isMinimized }"
  >
    <div class="video-player-card">
      <!-- Header bar -->
      <div
        class="header-bar"
        @click="isMinimized ? maximize() : null"
      >
        <span class="video-title">{{ title }}</span>
        <div class="button-group">
          <button 
            class="control-button"
            :aria-label="isMinimized ? 'Maximize video player' : 'Minimize video player'"
            @click.stop="toggleMinimize"
          >
            <svg
              class="control-icon"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
          
          <!--close button 
          <button 
            class="control-button"
            aria-label="Close video player"
            @click.stop="$emit('close')"
          >
            <svg class="control-icon" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
          -->
        </div>
      </div>

      <!-- Video content section -->
      <div
        v-show="!isMinimized"
        class="video-content"
      >
        <div class="video-wrapper">
          <!-- Video element with poster -->
          <video 
            ref="videoPlayer"
            class="video-element"
            controls
            playsinline
            :poster="thumbnailUrl"
            @play="isPlaying = true"
            @pause="isPlaying = false"
          >
            <source
              :src="videoSource"
              type="video/mp4"
            >
            Your browser does not support the video tag.
          </video>

          <!-- Custom thumbnail overlay -->
          <div 
            v-if="!isPlaying" 
            class="thumbnail-overlay"
            :style="thumbnailStyle"
          >
            <button
              class="play-button"
              @click="togglePlay"
            >
              <svg
                class="play-icon"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FloatingVideoPlayer',
  props: {
    videoSource: {
      type: String,
      required: true
    },
    thumbnailUrl: {
      type: String,
      default: '/api/placeholder/320/180'
    },
    title: {
      type: String,
      default: 'Video Player'
    }
  },
  data() {
    return {
      isPlaying: false,
      isMinimized: false
    }
  },
  computed: {
    thumbnailStyle() {
      return {
        backgroundImage: `url(${this.thumbnailUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    }
  },
  methods: {
    togglePlay() {
      if (this.isPlaying) {
        this.$refs.videoPlayer.pause()
      } else {
        this.$refs.videoPlayer.play()
      }
    },
    toggleMinimize() {
      if (!this.isMinimized) {
        this.minimize()
      } else {
        this.maximize()
      }
    },
    minimize() {
      if (this.isPlaying) {
        this.$refs.videoPlayer.pause()
      }
      this.isMinimized = true
    },
    maximize() {
      this.isMinimized = false
    }
  }
}
</script>

<style scoped>
.floating-video-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  width: 480px;
  transition: all 0.3s ease;
}

.floating-video-container.minimized {
  width: 200px;
}

.video-player-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.video-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
}

.video-element {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

/* Header styles */
.header-bar {
  padding: 8px 12px;
  background: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.minimized .header-bar {
  border-bottom: none;
}

.video-title {
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
}

.button-group {
  display: flex;
  gap: 4px;
}

.control-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.control-button:hover {
  color: #333;
}

.control-icon {
  width: 20px;
  height: 20px;
  fill: currentColor;
}

/* Video content section */
.video-content {
  transition: all 0.3s ease;
}

/* Thumbnail overlay styles */
.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.2);
  transition: background-color 0.3s ease;
}

.thumbnail-overlay:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.play-button {
  position: relative;
  width: 64px;
  height: 64px;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.8);
}

.play-button:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.1);
}

.play-icon {
  width: 32px;
  height: 32px;
  fill: white;
}

/* Animation classes */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.floating-video-container {
  animation: slideIn 0.3s ease-out;
}
</style>